import { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import ControlButton from '../../activities/elements/ControlButton';
import ConditionalRenderer from './ConditionalRenderer';

interface Props extends HTMLAttributes<HTMLDivElement> {
  hidePrev?: boolean;
  isOnLast?: boolean;
  prevText?: string;
  nextText?: string;
  buttonColor?: 'text-base-content' | 'text-primary';
  goNext: () => void;
  goBack?: () => void;
  onFinish?: () => void;
  isNextRequest?: boolean;
  isPrevRequest?: boolean;
  className?: string;
  hideNext?: boolean;
  disabled?: boolean;
  submitting?: boolean;
}

export default function Controls(props: Props) {
  const {
    goNext,
    goBack,
    onFinish,
    hidePrev,
    isOnLast,
    isNextRequest,
    prevText,
    buttonColor,
    isPrevRequest,
    nextText,
    hideNext,
    className,
    disabled,
    submitting,
  } = props;

  const { t: tControls } = useTranslation('translation', {
    keyPrefix: 'lesson.controlButton',
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'common',
  });

  return (
    <nav
      className={twMerge(
        'flex w-full',
        className,
        hidePrev ? 'justify-end' : 'justify-between',
        disabled ? 'pointer-events-none opacity-60' : '',
      )}
    >
      <ConditionalRenderer condition={!hidePrev}>
        <ControlButton.Root
          className={buttonColor}
          onClick={goBack}
          testId="controlPrev"
          disabled={isPrevRequest}
        >
          <ControlButton.Icon
            icon={<ChevronLeftIcon className="w-5 h-5" />}
            isLoading={isPrevRequest}
          />
          <ControlButton.Text text={prevText ?? t('previous')} />
        </ControlButton.Root>
      </ConditionalRenderer>

      <ConditionalRenderer condition={!hideNext}>
        <ControlButton.Root
          type={submitting && isOnLast ? 'submit' : 'button'}
          className="text-secondary"
          onClick={isOnLast ? onFinish : goNext}
          testId="controlNext"
          disabled={isNextRequest}
        >
          <ControlButton.Text
            text={isOnLast ? tControls('finish') : nextText ?? t('next')}
          />
          <ControlButton.Icon
            icon={<ChevronRightIcon className="w-5 h-5" />}
            isLoading={isNextRequest}
          />
        </ControlButton.Root>
      </ConditionalRenderer>
    </nav>
  );
}
